// ═══════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM · cores, tipografia e a marca
// ═══════════════════════════════════════════════════════════════════════
// Mudou de casa em 07/10/2026, junto com o transporte. Uma definição só para
// as duas interfaces: mudar o verde aqui muda no Business e na Rotina
// Contábil, que é o que torna as duas reconhecíveis como a mesma casa.

import React from 'react';

const G = {
  verde:       '#1B6B2F',
  verdeLight:  '#E8F5EC',
  verdeMid:    '#2D8A45',
  laranja:     '#F47C3C',
  laranjaLight:'#FFF0E6',
  fundo:       '#FAFAF7',
  card:        '#FFFFFF',
  txt1:        '#1A1A1A',
  txt2:        '#6B7280',
  borda:       '#E5E7EB',
  vermelho:    '#DC2626',
  vermelhoL:   '#FEF2F2',
  // Azul de investimento/DRE (barras do Dashboard · linha CAPEX do plano) —
  // entrou no token em 01/09; antes vivia hardcoded (nota da auditoria).
  azul:        '#1E5BAA',
};

// ─────────────────────────────────────────────────────────────────────────
// TIPO — Tipografia padrão de hierarquia de texto auxiliar.
// Validado em 11/06/2026 na Tarefa 1 do BP60M. Aplicar em todas as telas
// onde aparece subtítulo de tela / sub-subtítulo inline / descrição em card.
// Doc: memory/tipografia-padrao.md
// ─────────────────────────────────────────────────────────────────────────
const TIPO = {
  // Parágrafo abaixo do H2 da tela (subtítulo principal)
  subtitulo:     { fontSize: 14, color: G.txt2, lineHeight: 1.5 },
  // Texto auxiliar inline (ex: após " · " no eyebrow)
  subSubtitulo:  { fontSize: 12, color: G.txt2 },
  // Texto dentro de cards de seleção / info contextual
  descricaoCard: { fontSize: 13, color: G.txt2, lineHeight: 1.45 },
};

// ─────────────────────────────────────────────────────────────────────────
// Marca registrada: "RUNGOAL AIDVISOR" — AI laranja + DVISOR verde.
// "AI" destaca o conceito de Inteligência Artificial (sigla EN correta);
// "DVISOR" mantém a continuidade visual da marca-mãe RunGoal (foundation).
// Em STRINGS puras (system prompt da IA, logs, alt-text) → "RUNGOAL AIDVISOR".
// Em UI visual → usar este componente para preservar a cor.
//
// Variantes:
//   - "default"  → RunGoal + DVISOR verde · AI laranja (uso geral em UI claro)
//   - "inverse"  → RunGoal + DVISOR laranja · AI verde (raro)
//   - "onDark"   → RunGoal + DVISOR branco · AI laranja (fundos verde escuro · overlay loading)
//   - "onOrange" → RunGoal + DVISOR branco · AI verde (fundos laranja vibrante)
// ─────────────────────────────────────────────────────────────────────────
const MarcaAIdvisor = ({ variant = 'default', bold = true, style = {} }) => {
  const cores = {
    default:  { base: G.verde,   ai: G.laranja },
    inverse:  { base: G.laranja, ai: G.verde   },
    onDark:   { base: '#FFF',    ai: G.laranja },
    onOrange: { base: '#FFF',    ai: G.verde   },
  }[variant] || { base: G.verde, ai: G.laranja };
  const baseStyle = { fontWeight: bold ? 800 : 'inherit', ...style };
  return (
    <span style={baseStyle}>
      <span style={{ color: cores.base }}>RUNGOAL </span>
      <span style={{ color: cores.ai }}>AI</span>
      <span style={{ color: cores.base }}>DVISOR</span>
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// NÚMEROS — dinheiro sempre com 2 casas, declarando mínimo E máximo.
// Forma canônica do design system (seção 10.1). Subiu para cá em 07/10/2026,
// quando a Rotina Contábil virou repositório próprio e passou a precisar do
// mesmo formatador: duas cópias de um formatador de dinheiro divergem, e a
// divergência aparece como centavo a menos na tela do contador.
// O gêmeo que vive no App.js do Business sai de lá quando o Business perder a
// modalidade Contábil. Até então ele vence dentro do App.js, que não importa
// este nome — acrescentá-lo ao import de lá viraria erro de duplicidade.
// ─────────────────────────────────────────────────────────────────────────
const fmtBRL = v => v == null ? 'N/D' :
  'R$ ' + Math.abs(v).toLocaleString('pt-BR',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export { G, TIPO, MarcaAIdvisor, fmtBRL };
